import React from 'react';

export interface DisconnectIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 20). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Disconnect glyph — a cross inside a rounded Bombiix-red chip, used on the
 * directory disconnect control. Decorative by default (aria-hidden); pass `title`
 * when the control has no adjacent text label.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the disconnect icon SVG element.
 */
function DisconnectIcon({ size = 20, className = '', title, ...rest }: DisconnectIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect width="20" height="20" rx="4" fill="var(--color-red-bg)" />
      <path
        d="M11.6253 11.6243L10.0003 9.99935M10.0003 9.99935L8.37533 8.37435M10.0003 9.99935L8.37533 11.6243M10.0003 9.99935L11.6253 8.37435M4.04199 9.99935C4.04199 13.29 6.70963 15.9577 10.0003 15.9577C13.291 15.9577 15.9587 13.29 15.9587 9.99935C15.9587 6.70865 13.291 4.04102 10.0003 4.04102C6.70963 4.04102 4.04199 6.70865 4.04199 9.99935Z"
        stroke="var(--color-red)"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(DisconnectIcon);
